import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Search, CalendarClock, Info, FileDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const num = (n) => (Number(n) || 0).toLocaleString("id-ID");

export default function PreOrderQuota() {
  const { can } = useAuth();
  const write = can("warehouse_admin", "sales");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [products, setProducts] = useState([]);
  const [edit, setEdit] = useState(null);
  const [sync, setSync] = useState(false);
  const [batchF, setBatchF] = useState("all");
  const [batches, setBatches] = useState([]);

  const load = () => api.get("/preorders/quotas", { params: { q: q || undefined, status: statusF, batch_id: batchF } })
    .then((r) => setRows(r.data.items || [])).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF, batchF]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => { api.get("/products/inventory").then((r) => setProducts(r.data.products || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/preorder-batches", { params: { status: "all" } }).then((r) => setBatches(r.data.items || [])).catch(() => {}); }, []);

  const remove = async (row) => {
    if (!window.confirm(`Hapus kuota ${row.product_name} ${row.color}/${row.size}?`)) return;
    try { await api.delete(`/preorders/quotas/${row.id}`); toast.success("Kuota dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="preorder-quota-page">
      <PageHeader title="Kuota Pre-Order" subtitle="Kuota PRE_ORDER per varian — tidak memakai stok fisik gudang">
        <Button variant="outline" onClick={() => downloadFile("/preorders/quotas/export", { q: q || undefined, status: statusF, batch_id: batchF }, "kuota-preorder.xlsx").catch((e) => toast.error(formatApiError(e.response?.data?.detail)))} className="border-status-passed text-status-passed hover:bg-status-passed/5 mr-2" data-testid="po-export-btn"><FileDown className="w-4 h-4 mr-1.5" /> Export</Button>
        {write && <Button variant="outline" onClick={() => setSync(true)} className="border-cocoa text-cocoa mr-2" data-testid="po-sync-btn"><RefreshCw className="w-4 h-4 mr-1.5" /> Sinkronkan Reserved</Button>}
        {write && <Button onClick={() => setEdit({})} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="po-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Tambah Kuota</Button>}
      </PageHeader>

      <div className="mb-4 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-blue-900 text-sm" data-testid="po-info-banner">
        <Info className="w-5 h-5 mt-0.5 shrink-0 text-blue-600" />
        <span>PRE_ORDER adalah bucket penjualan berbasis <b>kuota</b>, bukan gudang fisik. Angka ini tidak menambah On Hand, tidak mengambil stok gudang, dan tidak masuk QC/In Transit. <b>Remaining = Planned − Keep − Sold</b> (Keep = keep aktif riil; bila angka tak pas, jalankan <b>Sinkronkan Reserved</b>).</span>
      </div>

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="relative w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk / warna / ukuran..." className="pl-9 bg-white" data-testid="po-search" />
        </div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-40 bg-white" data-testid="po-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Status</SelectItem><SelectItem value="Open">Open</SelectItem><SelectItem value="Closed">Closed</SelectItem></SelectContent>
        </Select>
        <Select value={batchF} onValueChange={setBatchF}>
          <SelectTrigger className="w-64 bg-white" data-testid="po-batch-filter"><SelectValue placeholder="Semua Batch PO" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Semua Batch PO</SelectItem>
            {batches.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`po-batch-opt-${b.id}`}>{b.name || b.code}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-12">No</TableHead><TableHead>Produk</TableHead><TableHead>Varian</TableHead>
              <TableHead>Periode</TableHead>
              <TableHead className="text-right">Planned</TableHead><TableHead className="text-right">Datang</TableHead><TableHead className="text-right">Keep</TableHead>
              <TableHead className="text-right">Sold</TableHead><TableHead className="text-right">Remaining</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.id} data-testid={`po-row-${r.id}`}>
                <TableCell>{i + 1}</TableCell>
                <TableCell className="font-semibold text-cocoa">{r.product_name || "-"}</TableCell>
                <TableCell>{r.color}/{r.size}</TableCell>
                <TableCell className="text-sm text-wmuted">{r.period_label || "-"}</TableCell>
                <TableCell className="text-right">{num(r.planned_qty)}</TableCell>
                <TableCell className="text-right" data-testid={`po-arrived-${r.id}`}><span className="font-semibold text-cocoa">{num(r.arrived_qty)}</span><span className="text-[10px] text-wmuted">/{num(r.planned_qty)}</span></TableCell>
                <TableCell className="text-right text-amber-700" data-testid={`po-keep-${r.id}`}>{num(r.keep_aktif)}</TableCell>
                <TableCell className="text-right text-blue-700">{num(r.sold_po)}</TableCell>
                <TableCell className="text-right font-bold text-status-passed" data-testid={`po-remaining-${r.id}`}>{num(r.remaining_po)}</TableCell>
                <TableCell><Badge variant="outline" className={r.status === "Open" ? "text-status-passed border-green-300" : "text-wmuted"}>{r.status}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {write && <>
                    <Button size="icon" variant="ghost" onClick={() => setEdit(r)} className="h-8 w-8 text-cocoa" data-testid={`po-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(r)} className="h-8 w-8 text-status-reject" data-testid={`po-del-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </>}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={11} className="text-center text-wmuted py-10"><CalendarClock className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada kuota Pre-Order.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>

      {edit && <QuotaDialog quota={edit} products={products} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {sync && <SyncReservedDialog onClose={() => setSync(false)} onApplied={load} />}
    </div>
  );
}

function QuotaDialog({ quota, products, onClose, onSaved }) {
  const isEdit = !!quota.id;
  const [pid, setPid] = useState(quota.product_id || "");
  const [form, setForm] = useState({
    variant_id: quota.variant_id || "", planned_qty: quota.planned_qty ?? 0, reserved_po: quota.reserved_po ?? 0,
    sold_po: quota.sold_po ?? 0, period_label: quota.period_label || "", status: quota.status || "Open", note: quota.note || "",
  });
  const variants = useMemo(() => (products.find((p) => p.id === pid)?.variants) || [], [products, pid]);
  const remaining = Math.max((parseInt(form.planned_qty) || 0) - (parseInt(form.reserved_po) || 0) - (parseInt(form.sold_po) || 0), 0);

  const save = async () => {
    if (!form.variant_id) return toast.error("Pilih varian");
    const payload = { ...form, planned_qty: Number(form.planned_qty) || 0, reserved_po: Number(form.reserved_po) || 0, sold_po: Number(form.sold_po) || 0 };
    try {
      if (isEdit) await api.put(`/preorders/quotas/${quota.id}`, payload);
      else await api.post("/preorders/quotas", payload);
      toast.success(isEdit ? "Kuota diperbarui" : "Kuota dibuat"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="po-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{isEdit ? "Edit" : "Tambah"} Kuota Pre-Order</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label>Produk</Label>
            <Select value={pid || undefined} onValueChange={(v) => { setPid(v); setForm({ ...form, variant_id: "" }); }} disabled={isEdit}>
              <SelectTrigger data-testid="po-product-select"><SelectValue placeholder="Pilih produk" /></SelectTrigger>
              <SelectContent className="max-h-72">{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>Varian</Label>
            <Select value={form.variant_id || undefined} onValueChange={(v) => setForm({ ...form, variant_id: v })} disabled={isEdit}>
              <SelectTrigger data-testid="po-variant-select"><SelectValue placeholder="Pilih varian" /></SelectTrigger>
              <SelectContent className="max-h-72">{variants.map((v) => <SelectItem key={v.variant_id} value={v.variant_id}>{v.color}/{v.size}</SelectItem>)}</SelectContent>
            </Select>
            {isEdit && <p className="text-xs text-wmuted mt-1">{quota.product_name} — {quota.color}/{quota.size}</p>}
          </div>
          <div><Label>Planned Qty</Label><Input type="number" value={form.planned_qty} onChange={(e) => setForm({ ...form, planned_qty: e.target.value })} data-testid="po-planned" /></div>
          <div><Label>Reserved PO</Label><Input type="number" value={form.reserved_po} onChange={(e) => setForm({ ...form, reserved_po: e.target.value })} data-testid="po-reserved" /></div>
          <div><Label>Sold PO</Label><Input type="number" value={form.sold_po} onChange={(e) => setForm({ ...form, sold_po: e.target.value })} data-testid="po-sold" /></div>
          <div><Label>Remaining PO</Label><Input value={num(remaining)} disabled className="bg-taupe/10 font-bold" data-testid="po-remaining-preview" /></div>
          <div><Label>Periode</Label><Input value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} placeholder="mis. Batch Agustus" data-testid="po-period" /></div>
          <div><Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="po-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Open">Open</SelectItem><SelectItem value="Closed">Closed</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="po-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SyncReservedDialog({ onClose, onApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  const preview = () => {
    setLoading(true);
    api.post("/preorders/quotas/sync-reserved", { dry_run: true })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { preview(); /* eslint-disable-next-line */ }, []);

  const apply = async () => {
    setApplying(true);
    try {
      const r = await api.post("/preorders/quotas/sync-reserved", { dry_run: false });
      toast.success(`${r.data.applied} kuota disinkronkan`);
      onApplied();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  const changes = data?.changes || [];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="po-sync-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa flex items-center gap-2"><RefreshCw className="w-5 h-5" />Sinkronkan Reserved ke Keep Aktif</DialogTitle></DialogHeader>
        <p className="text-sm text-wmuted">Reserved PO tiap kuota akan dihitung ulang dari jumlah <b>keep aktif</b> (Active / Partially Converted). <b>Planned & Sold PO tidak diubah.</b></p>
        {loading ? (
          <div className="text-center py-8 text-wmuted" data-testid="po-sync-loading">Menghitung…</div>
        ) : !changes.length ? (
          <div className="text-center py-8 text-emerald-600" data-testid="po-sync-empty">✓ Semua kuota sudah sinkron. Tidak ada yang perlu diubah.</div>
        ) : (
          <>
            <div className="text-sm text-cocoa mt-1" data-testid="po-sync-summary">Ada <b>{data.count}</b> kuota yang berbeda. Total Reserved: <b>{num(data.total_before)}</b> → <b className="text-emerald-600">{num(data.total_after)}</b>.</div>
            <Panel className="p-0 overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk</TableHead><TableHead>Varian</TableHead><TableHead>Periode/Batch</TableHead><TableHead className="text-right">Reserved Lama</TableHead><TableHead className="text-right">Keep Aktif</TableHead><TableHead className="text-right">Selisih</TableHead></TableRow></TableHeader>
                <TableBody>
                  {changes.map((c) => (
                    <TableRow key={c.id} data-testid={`po-sync-row-${c.id}`}>
                      <TableCell className="font-medium text-cocoa">{c.product_name || "-"}</TableCell>
                      <TableCell className="text-sm">{c.color}/{c.size}</TableCell>
                      <TableCell className="text-sm text-wmuted">{c.period_label || "-"}</TableCell>
                      <TableCell className="text-right text-amber-700">{num(c.before)}</TableCell>
                      <TableCell className="text-right font-semibold">{num(c.after)}</TableCell>
                      <TableCell className={`text-right font-bold ${c.delta < 0 ? "text-status-reject" : "text-emerald-600"}`}>{c.delta > 0 ? "+" : ""}{num(c.delta)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Panel>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa" data-testid="po-sync-close">Tutup</Button>
          <Button onClick={apply} disabled={loading || applying || !changes.length} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="po-sync-apply">{applying ? "Menerapkan…" : `Terapkan (${data?.count || 0})`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
